import * as Popover from "@radix-ui/react-popover";
import { Auth0AccountRole } from "client/openapi";
import { Dialog } from "components/Dialog";
import MeetingDialog, { InitialMeetingDraft } from "components/MeetingDialog";
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Notifications from "util/notifications";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import CompactBookingForm from "./CompactBookingForm";
import { GutterBooking, GutterBookingContext } from "./context";
import DraggableContent from "./DraggableContent";
import "./index.css";

/**
 * Holds the time picked in the calendar's strip and shows the compact
 * booking pop-up for it, which its header drags anywhere in the window
 * (DraggableContent). It wraps the calendar but keeps the calendar
 * element it is given, so opening or closing the pop-up re-renders only
 * the day columns, never CalendarWrapper or its meetings.
 */
export default function GutterBookingProvider({
  role,
  refetch,
  dayView,
  viewKey,
  children,
}: {
  role: Auth0AccountRole;
  /** Reloads the calendar's meetings. */
  refetch: () => Promise<void>;
  /** In day view the column is wide, so the pop-up opens below the time. */
  dayView: boolean;
  /** Changes when the calendar shows other days. */
  viewKey: string;
  children: ReactNode;
}) {
  const { currently_selected_organization } = useContext(
    OrgRolesAndAccountContext
  );
  const [booking, setBooking] = useState<GutterBooking | null>(null);
  // Where the pop-up opened: it stays anchored there when a grid in it moves
  // the time, even to a day the calendar doesn't show.
  const [anchor, setAnchor] = useState<GutterBooking | null>(null);
  // Counts selections in the strip: each starts a new form. The form moving
  // its own time (from the rooms grid) keeps it.
  const [selection, setSelection] = useState(0);
  // Book Meeting opened from the pop-up's More options, with its values.
  const [moreOptions, setMoreOptions] = useState<InitialMeetingDraft | null>(
    null
  );

  // The picked time may no longer be on screen.
  useEffect(() => setBooking(null), [viewKey]);

  const openBooking = useCallback((picked: GutterBooking) => {
    setBooking(picked);
    setAnchor(picked);
    setSelection((n) => n + 1);
  }, []);

  const value = useMemo(
    () => ({ booking, anchor, openBooking }),
    [booking, anchor, openBooking]
  );

  return (
    <GutterBookingContext.Provider value={value}>
      <Popover.Root
        open={!!booking}
        onOpenChange={(open) => {
          if (!open) setBooking(null);
        }}
      >
        {children}
        {booking && currently_selected_organization && (
          <Popover.Portal>
            <DraggableContent
              // Each selection's pop-up opens beside it.
              resetKey={selection}
              className="gutter-booking-popover"
              side={dayView ? "bottom" : "left"}
              align={dayView ? "center" : "start"}
              sideOffset={8}
              collisionPadding={12}
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <CompactBookingForm
                key={selection}
                orgId={currently_selected_organization}
                start={booking.start}
                end={booking.end}
                onClose={() => setBooking(null)}
                onTimeChange={(start, end) => setBooking({ start, end })}
                onSaved={() => {
                  setBooking(null);
                  refetch();
                  Notifications.success("Meeting booked.");
                }}
                onMoreOptions={(draft) => {
                  setBooking(null);
                  setMoreOptions(draft);
                }}
              />
            </DraggableContent>
          </Popover.Portal>
        )}
      </Popover.Root>
      <Dialog
        open={!!moreOptions}
        onOpenChange={(open) => {
          if (!open) setMoreOptions(null);
        }}
      >
        {moreOptions && (
          <MeetingDialog
            isCreate
            role={role}
            initialDraft={moreOptions}
            setEvents={refetch}
          />
        )}
      </Dialog>
    </GutterBookingContext.Provider>
  );
}
